<!-- Components/dashboard/EmployeeCard.vue -->
<template>
  <div
       :class="cardClasses"
       role="button"
       tabindex="0"
       v-tooltip.bottom="rosterLog.event_reason && rosterLog.event_reason !== 'UNDEFINED' ? getEventReasonLabel(rosterLog.event_reason) : null"
       @click="showContextMenu"
       @contextmenu.prevent="showContextMenu"
       @keydown.enter.prevent="showContextMenu"
       @keydown.space.prevent="showContextMenu">
    <!-- Employee Photo -->
    <div class="employee-photo-small">
      <div class="employee-cover-status" v-if="showCoverStatus">COVER</div>
      <div class="employee-shift-status" v-else>{{ rosterLog.employee_company_name_short }}</div>
      <img :src="defaultUserImage" alt="Employee Photo"/>
      <!-- <img :src="rosterLog.employee_photo_url || defaultUserImage" alt="Employee Photo"/> -->
    </div>
    <!-- Employee Content -->
    <div class="employee-content">
      <!-- Employee Name -->
      <h3 class="employee-name">{{ rosterLog.employee_name }}</h3>
      <!-- Employee Company Name -->
      <p class="employee-shifttime" v-if="title=='COVERS'">{{ truncatedCompanyName }}</p>
      <!-- Shift: Check ONGOING or NOT LOGOUT -->
      <p class="employee-checkintime" v-if="title !='UPCOMING' && title !='LEAVES'">
        <template v-if="typeof checkShiftStatus === 'string'">
          {{ checkShiftStatus }}
        </template>
        <template v-else-if="checkShiftStatus && checkShiftStatus.time">
          {{ checkShiftStatus.time }} 
          <span class="status-text opacity-60 font-light">{{ checkShiftStatus.status }}</span>
        </template>
      </p>
      <!-- Shift: Show Shift Time -->
      <p class="employee-shifttime">{{ shiftTimeDisplay }}</p>
      <!-- Shift: Early Time Counter (Shift IN)-->
      <div v-if="rosterLog.attendance_status_in==='EARLY'">
        <p class="employee-timecounter" v-if="showCounter==true && title=='EARLY'">
          <i class="pi pi-arrow-up icon"></i> 
          {{ calculateTimeRemaining(rosterLog.attendance_in, rosterLog.event_start, 'm') }}
        </p>
      </div>
      <!-- Shift: Early Time Counter (Shift OUT)-->
      <div v-else-if="rosterLog.attendance_status_out==='EARLY'">
        <p class="employee-timecounter" v-if="showCounter==true && title=='EARLY'">
          <i class="pi pi-arrow-down icon"></i> 
          {{ calculateTimeRemaining(rosterLog.attendance_out, rosterLog.event_end, 'm') }}
        </p>
      </div>
      <!-- Shift: On Time Counter -->
      <p class="employee-timecounter" v-else-if="showCounter==true && title=='ONTIME'">
        <i class="pi pi-arrow-up icon"></i> 
        {{ calculateTimeRemaining(rosterLog.attendance_in, rosterLog.event_start, 'm') }}
      </p>
      <!-- Shift: Late Time Counter -->
      <p class="employee-timecounter" v-else-if="showCounter==true && title=='LATE'">
        <i class="pi pi-arrow-down icon"></i> 
        {{ calculateTimeRemaining(rosterLog.event_start, rosterLog.attendance_in, 'm') }}
      </p>
      <!-- Shift: Over Time Counter -->
      <p class="employee-timecounter" v-else-if="showCounter==true && title=='OVERTIME'">
        <i class="pi pi-arrow-up icon"></i> 
        {{ calculateTimeDifferenceInHours(rosterLog.event_end, rosterLog.attendance_out, 'h') }}
      </p>
      <!-- Roster: Upcoming Shift Time Counter -->
      <p class="employee-timecounter" v-if="showCounter && title === 'UPCOMING'">{{ displayCountdown }}</p>
      <!-- Shift: Not Logout Time Counter -->
      <p class="employee-timecounter" v-else-if="showCounter==true && title=='NOTLOGOUT'">
        {{ calculateTimeRemaining(rosterLog.event_end, generateCurrentTimeunix, 'm') }}
      </p>
      <!-- Shift: Worked Hours Counter -->
      <p class="employee-timecounter workedcounter" 
         v-else-if="showCounter==true && rosterLog.attendance_out !== null && title !='LEAVES'"
         :class="workedHoursStatusClass">
         <i v-if="workedHoursDisplay === 'ERROR'" class="pi pi-exclamation-triangle"></i> 
         <i v-else-if="workedHoursDisplay === 'OPEN'" class="pi pi-exclamation-triangle"></i>
         <i v-else class="pi pi-check"></i>
         {{ workedHoursDisplay }}
      </p>
      <!-- Roster: Leaves Indicator -->
      <p class="employee-timecounter" v-else-if="title =='LEAVES' && rosterLog.event_type !== 'SHIFT' && rosterLog.event_type !== 'COVER'">L</p>
      <!-- Roster: No Show Indicator -->
      <p class="employee-timecounter" v-else-if="title =='LEAVES' && (rosterLog.event_type == 'SHIFT' || rosterLog.event_type == 'COVER')">N</p>
    </div>
  </div>

  <ContextMenu ref="contextMenu" :model="contextMenuItems" />

  <!-- Logs Dialog -->
  <Dialog v-model:visible="viewLogs" :style="{ width: '60vw' }" :modal="true">
    <template #header>
      <h2>Logs</h2>
    </template>
    <LogsDialog :selectedIds="rosterLog.event_id" logType="roster" @handleBack="handleBack" />
  </Dialog>
</template>

<script setup>
import { ref, computed, onUnmounted, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTimeCalculation } from '@/composables/time/useTimeCalculation';
import { useLondonUnixTime } from '@/composables/time/useLondonTime';
import { useUtils } from '@/composables/useUtils';
import LogsDialog from '@/components/dialogs/LogsDialog.vue';
import defaultUserImage from '@/assets/default-user.png';
import Dialog from 'primevue/dialog';
import ContextMenu from 'primevue/contextmenu';

const { generateCurrentTimeunix } = useLondonUnixTime();
const { calculateNetWorkedHours } = useUtils();

const SHIFT_REASONS = [
  { label: 'Undefined', value: 'UNDEFINED' },
  { label: 'Death in Family', value: 'DEATH_IN_FAMILY_LEAVE' },
  { label: 'Casual Leave', value: 'CASUAL_LEAVE' },
  { label: 'Emergency Leave', value: 'EMERGENCY_LEAVE' },
  { label: 'Wedding Leave', value: 'WEDDING_LEAVE' },
  { label: 'Birthday Leave', value: 'BIRTHDAY_LEAVE' },
  { label: 'Uninformed Leave', value: 'UNINFORMED_LEAVE' },
  { label: 'Road Accident', value: 'ROAD_ACCIDENT_LEAVE' },
  { label: 'National Holiday', value: 'NATIONAL_HOLIDAY_LEAVE' },
  { label: 'Sick Leave', value: 'SICK_LEAVE' },
  { label: 'Put on Hold', value: 'PUT_ONHOLD_LEAVE' }
];

// Function to get event reason label
const getEventReasonLabel = (value) => {
  const reason = SHIFT_REASONS.find(reason => reason.value === value);
  return reason ? reason.label : 'Unknown Reason';
};

const viewLogs = ref(false);
const contextMenu = ref(null);
const route = useRoute();
const router = useRouter();

// Props
const props = defineProps({
  title: String,
  rosterLog: Object,
  showCounter: Boolean
});

// Utils: Time Calculation
const { calculateTimeRemaining, formatTime, calculateTimeDifferenceInHours } = useTimeCalculation()

const getNavigationIds = () => {
  const attendanceId = Number(props.rosterLog.attendance_id_in);
  const employeeId = Number(props.rosterLog.employee_id);
  const rosterId = Number(props.rosterLog.event_id);
  return { attendanceId, employeeId, rosterId };
};

const hasValidRosterNavigation = ({ employeeId, rosterId }) => (
  Number.isSafeInteger(employeeId) && employeeId > 0
  && Number.isSafeInteger(rosterId) && rosterId > 0
);

const hasAttendance = computed(() => {
  const attendanceId = Number(props.rosterLog.attendance_id_in);
  return Number.isSafeInteger(attendanceId) && attendanceId > 0;
});

const canManageAttendance = computed(() => (
  ['SHIFT', 'COVER'].includes(props.rosterLog.event_type)
));

const editRoster = () => {
  const returnTo = route.name === 'DashboardDay' ? route.fullPath : undefined;
  const ids = getNavigationIds();
  if (!hasValidRosterNavigation(ids)) return;

  router.push({
    name: 'EditRoster',
    params: { rosterIds: ids.rosterId },
    query: returnTo ? { returnTo } : undefined
  });
};

const editAttendance = () => {
  const returnTo = route.name === 'DashboardDay' ? route.fullPath : undefined;
  const ids = getNavigationIds();
  if (!hasValidRosterNavigation(ids) || props.rosterLog.event_type === 'LEAVE') return;

  if (Number.isSafeInteger(ids.attendanceId) && ids.attendanceId > 0) {
    router.push({
      name: 'EditAttendance',
      params: {
        employeeId: ids.employeeId,
        rosterId: ids.rosterId,
        attendanceId: ids.attendanceId
      },
      query: returnTo ? { returnTo } : undefined
    });
    return;
  }

  if (['SHIFT', 'COVER'].includes(props.rosterLog.event_type)) {
    router.push({
      name: 'MarkAttendance',
      params: { rosterId: ids.rosterId },
      query: returnTo ? { returnTo } : undefined
    });
  }
};

const contextMenuItems = computed(() => [
  {
    label: props.rosterLog.event_type === 'LEAVE' ? 'Edit Leave' : 'Edit Roster',
    icon: 'pi pi-calendar',
    command: editRoster
  },
  {
    label: hasAttendance.value ? 'Edit Attendance' : 'Mark Attendance',
    icon: hasAttendance.value ? 'pi pi-pencil' : 'pi pi-check',
    visible: canManageAttendance.value,
    command: editAttendance
  }
]);

const showContextMenu = event => {
  if (!contextMenu.value) return;

  if (Number.isFinite(event.pageX) && Number.isFinite(event.pageY)) {
    contextMenu.value.show(event);
    return;
  }

  const targetBounds = event.currentTarget?.getBoundingClientRect();
  contextMenu.value.show({
    pageX: (targetBounds?.left || 0) + window.scrollX,
    pageY: (targetBounds?.bottom || 0) + window.scrollY,
    stopPropagation: () => event.stopPropagation(),
    preventDefault: () => event.preventDefault()
  });
};

// Computed: Check if upcoming shift
const isUpcoming = computed(() => props.title === 'UPCOMING');

// Roster: Upcoming Shift Time Counter
const countdownValue = ref('00:00:00');
let intervalId = null;

// Computed: Check if show counter
const shouldShowCounter = computed(() => props.showCounter && isUpcoming.value);

// Function: Update countdown
const updateCountdown = () => {
  if (shouldShowCounter.value && props.rosterLog) {
    countdownValue.value = calculateTimeRemaining(generateCurrentTimeunix, props.rosterLog.event_start, 'm');
  }
};

// Function: Reset and start countdown
const resetAndStartCountdown = () => {
  clearInterval(intervalId);
  updateCountdown();
  intervalId = setInterval(updateCountdown, 60000);
};

// Use watchEffect for more concise reactivity
watchEffect(() => {
  if (shouldShowCounter.value) {
    resetAndStartCountdown();
  } else {
    clearInterval(intervalId);
  }
});

// Unmount: Clear interval
onUnmounted(() => clearInterval(intervalId));

// Computed: Display countdown
const displayCountdown = computed(() => shouldShowCounter.value ? countdownValue.value : '');

// Function: Handle back
const handleBack = () => {
  viewLogs.value = false;
};

// Computed: Calculate worked hours
const workedHoursDisplay = computed(() => {
  return calculateNetWorkedHours(
    props.rosterLog.event_start,
    props.rosterLog.event_end,
    props.rosterLog.attendance_in,
    props.rosterLog.attendance_out,
    props.rosterLog.attendance_status_in,
    props.rosterLog.attendance_status_out,
    props.rosterLog.over_below_status
  );
});

// Computed: assign class to worked hours based on status
const workedHoursStatusClass = computed(() => ({
  'worked-hours-error': workedHoursDisplay.value === 'ERROR',
  'worked-hours-open': workedHoursDisplay.value === 'OPEN',
  'worked-hours-attention flash-in-out': props.rosterLog.employee_company_id !== 1 && workedHoursDisplay.value === 'OPEN'
}));

// Define card classes for different statuses
const cardClasses = computed(() => {
  // Calculate time to start
  const timeToStart = calculateTimeRemaining(generateCurrentTimeunix, props.rosterLog.event_start);
  return {
    // Base style
    'employee-card': true,
    'flex': true,
    'w-full': true,

    // Roster: if upcoming shift is between 1 hour and 3 minutes
    'alert': props.title === 'UPCOMING' && timeToStart <= 60 && timeToStart > 3,
    // Roster: if upcoming shift is between 3 minutes and 1 minute
    'alert2': props.title === 'UPCOMING' && timeToStart <= 3,

    // Leaves: if shift or cover
    'warning': props.title === 'LEAVES' && 
              (props.rosterLog.event_type === 'SHIFT' || props.rosterLog.event_type === 'COVER'),

    // Overtime: if not approved
    'notapproved': props.title === 'OVERTIME' && props.rosterLog.over_below_status !== true,

    // Early: if employee is early for shift
    'early2': props.title === 'EARLY' && 
             props.rosterLog.attendance_status_in === 'EARLY' && 
             props.rosterLog.event_type === 'SHIFT'
  };
});

// Computed: Check if cover shift
const showCoverStatus = computed(() => 
  props.rosterLog.shift_type_in === 'COVER' || props.rosterLog.event_type === 'COVER'
);

// Computed: Check if shift is ongoing or not logout
const checkShiftStatus = computed(() => {
  if (!props.rosterLog.attendance_in) return null;

  const baseTime = formatTime(props.rosterLog.attendance_in);

  if (props.title !== 'NOTLOGOUT' && props.rosterLog.attendance_out !== null) {
    return `${baseTime} to ${formatTime(props.rosterLog.attendance_out)}`;
  } else if (props.title !== 'NOTLOGOUT') {
    return {
      time: baseTime,
      status: 'ON GOING'
    };
  } else if (props.title === 'NOTLOGOUT') {
    return {
      time: baseTime,
      status: 'NOT LOGOUT'
    };
  }
  return baseTime;
});

// Computed: Truncate company name
const truncatedCompanyName = computed(() => {
  if (!props.rosterLog.employee_company_name) return '';
  return props.rosterLog.employee_company_name.length > 20 
    ? props.rosterLog.employee_company_name.substring(0, 20) + '.'
    : props.rosterLog.employee_company_name;
});

// Computed: Format Shift time
const shiftTimeDisplay = computed(() => {
  return `${formatTime(props.rosterLog.event_start)} to ${formatTime(props.rosterLog.event_end)}`;
});

</script>


<style scoped>
.dashboard-sections .employee-card {
  opacity: 1;
}
.dashboard-sections .employee-card:hover {
  background-color: #494949;
  cursor: pointer;
}
.dashboard-sections .employee-card {
  position: relative;
  display: flex;
  justify-content: space-between;
  background-color: #666;
  border-radius: 2px;
  margin-bottom: 4px;
  height: auto;
}
.dashboard-sections .upcoming .employee-card, .dashboard-sections .leaves .employee-card{
  height: 46px;
}
.dashboard-sections .upcoming .employee-content, .dashboard-sections .leaves .employee-content{
  padding-top: 8px;
}
.dashboard-sections .employee-photo-small img {
  width: 41px;
  max-height: 48px;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  border-radius: 3px 0px 0 3px;
}
.dashboard-sections .employee-photo-small {
  position: relative;
  margin-right: 6px;
}
.dashboard-sections .employee-content {
  text-align: left;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 5px 0 0 0;
  font-size: 12px;
  letter-spacing: 1px;
  color: #ffffffc9;
}
.dashboard-sections .employee-name {
  margin: 0 0 1px 0;
  padding: 0;
  font-size: 0.8rem !important;
  letter-spacing: -0.5px;
  font-weight: bold;
  color: #ffffffc9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dashboard-sections .employee-checkintime {
  margin: 0;
  padding: 0;
  font-weight: bold;
}
.dashboard-sections .employee-shifttime {
  margin: 0;
  padding: 0;
  opacity: 0.5;
  font-weight: bold;
}
.dashboard-sections .employee-overtimestatus {
  margin: 0;
  opacity: 0.5;
  position: absolute;
  right: 0;
  top: 28px;
  font-size: 8px;
  padding: 4px 6px;
  background-color: #000;
  text-transform: uppercase;
}
.dashboard-sections .employee-noshowstatus {
  margin: 0;
  padding: 0;
  opacity: 0.5;
  position: absolute;
  right: -1px;
  top: 28px;
  font-size: 8px;
  padding: 4px 3px;
  background-color: #000;
}
.dashboard-sections .employee-cover-status{
  position: absolute;
  background-color: #00caa5;
  bottom: 0;
  font-size: 8px;
  padding: 0px;
  color: #000;
  font-weight: bold;
  width: 100%;
  text-align: center;
  letter-spacing: 1px;
}
.dashboard-sections .employee-shift-status{
  position: absolute;
  letter-spacing: 0px;
  background-color: #ffffff7d;
  bottom: 0;
  font-size: 10px;
  color: #000;
  font-weight: bold;
  text-align: center;
  padding: 0;
  width: 100%;
}
.dashboard-sections .employee-overtime-status{
  position: absolute;
  letter-spacing: 1px;
  background-color: #f69700;
  bottom: 0;
  font-size: 8px;
  color: #000;
  font-weight: bold;
  text-align: center;
  padding: 2px;
  width: 100%;
}

.dashboard-sections .employee-timecounter {
  position: absolute;
  width: auto;
  text-align: center;
  margin: 0;
  padding: 0;
  color: #ffffff9b;
  font-weight: bold;
  bottom: 0;
  right: 0;
  text-align: right;
  background-color: #ffffff29;
  padding: 2px 4px;
  font-size: 12px;
  letter-spacing: 0;
  min-width: 56px;
  text-align: center;
}
.dashboard-sections .employee-timecounter i {
  font-size: 10px;
}

.dashboard-sections .workedcounter{
  bottom: 20px;
}
.dashboard-sections .covers .workedcounter{
  bottom: 0;
}
.dashboard-sections .covers .employee-card{
  height: auto;
}

.dashboard-sections .upcoming .warning{
  background-color: #906b0c;
}
.dashboard-sections .upcoming .warning:hover{
  background-color: #c48d00;
}





.dashboard-sections .alert{
  background-color: #58602f !important;
}
.dashboard-sections .alert:hover{
  background-color: #667712 !important;
}
.dashboard-sections .alert2{
  background-color: #692000 !important;
}
.dashboard-sections .alert2:hover{
  background-color: #ac3400 !important;
}

.dashboard-sections .ontime .employee-card{
  background-color: #466c46;
}
.dashboard-sections .ontime .employee-card:hover{
  background-color: #227c22;
}
.dashboard-sections .late .employee-card{
  background-color: #6e4a24;
}
.dashboard-sections .late .employee-card:hover{
  background-color: #c46500;
}
.dashboard-sections .leaves .employee-card{
  background-color: #65466c;
}
.dashboard-sections .leaves .employee-card:hover{
  background-color: #ae15d0;
}
.dashboard-sections .warning{
  background-color: rgb(137, 4, 4) !important;
}
.dashboard-sections .warning:hover{
  background-color: rgb(206, 12, 12) !important;
}
.dashboard-sections .overtime .employee-card{
  background-color: #46516c;
}
.dashboard-sections .overtime .employee-card:hover{
  background-color: #1748c6;
}
.dashboard-sections .overtime .notapproved{
  background-color: #363636 !important;
}
.dashboard-sections .overtime .notapproved:hover{
  background-color: #1748c6 !important;
}
.dashboard-sections .early .employee-card{
  background-color: #9a392b;
}
.dashboard-sections .early .employee-card:hover{
  background-color: #891200;
}
.dashboard-sections .early2{
  background-color: #6f372f !important;
}
.dashboard-sections .early2:hover{
  background-color: #7b1000 !important;
}
.dashboard-sections .notlogout .employee-card{
  background-color: #5d2c3e;
  animation: opacityAnimation 2s infinite ease-in-out;
}
.dashboard-sections .notlogout .employee-card:hover{
  background-color: rgb(133, 38, 105);
}
.dashboard-sections .covers .employee-card{
  background-color: #107161;
}
.dashboard-sections .covers .employee-card:hover{
  background-color: #00a186;
}
.p-button{
    width: 1rem;
    height: 1rem;
    border-radius: 3px;
    padding: 10px 13px;
    margin-bottom: 3px;
    margin-right: 2px;
    background-color: rgba(255, 255, 255, 0.314) !important
}

/* Add media query for screens smaller than 1500px */
@media screen and (max-width: 1650px) {
  .dashboard-sections .employee-name {
    max-width: 150px; /* Adjust this value to fit approximately 18 characters */
  }
}

/* Add media query for screens larger than 1600px */
@media screen and (min-width: 1651px) {
  .dashboard-sections .employee-name {
    max-width: none; /* Allow full width */
  }
}

.worked-hours-error {
  background-color: #ff4444 !important;
}

.worked-hours-attention {
  background-color: #ff0000 !important;
}

</style>

<style>

.dashboard-sections .employee-card .p-button-icon{
  font-size: 12px !important;
}
</style>
